import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from './store/authStore'
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute'
import AppRoute from './components/AppRoute/AppRoute'
import LandingPage from './pages/Landing/LandingPage'
import DesignersLandingPage from './pages/Landing/DesignersLandingPage'
import './App.css'

// Lazy-loaded pages — loaded on demand
const Layout = lazy(() => import('./components/Layout/Layout'))
const LoginPage = lazy(() => import('./pages/Login/LoginPage'))
const RegisterPage = lazy(() => import('./pages/Register/RegisterPage'))
const VerifyPage = lazy(() => import('./pages/Verify/VerifyPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword/ResetPasswordPage'))
const VkAuthCallbackPage = lazy(() => import('./pages/Auth/VkAuthCallbackPage'))
const GoogleAuthCallbackPage = lazy(() => import('./pages/Auth/GoogleAuthCallbackPage'))
const HistoryPage = lazy(() => import('./pages/History/HistoryPage'))
const ChatPage = lazy(() => import('./pages/Chat/ChatPage'))
const HomePage = lazy(() => import('./pages/Home/HomePage'))
const StudioPage = lazy(() => import('./pages/Studio/StudioPage'))
const VideoPage = lazy(() => import('./pages/Video/VideoPage'))
const AIAssistantPage = lazy(() => import('./pages/AIAssistant/AIAssistantPage'))
const AccountPage = lazy(() => import('./pages/Account/AccountPage'))
const FavoritesPage = lazy(() => import('./pages/Favorites/FavoritesPage'))
const HelpPage = lazy(() => import('./pages/Help/HelpPage'))
const ProductCardsPage = lazy(() => import('./pages/ProductCards/ProductCardsPage'))
const IllustrationPage = lazy(() => import('./pages/Illustration/IllustrationPage'))
const IdeogramStudioPage = lazy(() => import('./pages/IdeogramV2/IdeogramStudioPage'))
const InfographicEditorPage = lazy(() => import('./pages/InfographicEditor/InfographicEditorPage'))
const VideoContentPage = lazy(() => import('./pages/VideoContent/VideoContentPage'))
const AiAvatarPage = lazy(() => import('./pages/AiAvatar/AiAvatarPage'))
const BalancePage = lazy(() => import('./pages/Balance/BalancePage'))
const AIChatPage = lazy(() => import('./pages/AIChat/AIChatPage'))
const LibraryPage = lazy(() => import('./pages/Library/LibraryPage'))
const LibraryHubPage = lazy(() => import('./pages/LibraryHub/LibraryHubPage'))
const SupportPage = lazy(() => import('./pages/Support/SupportPage'))
const SupportNewPage = lazy(() => import('./pages/Support/SupportNewPage'))
const SupportTicketPage = lazy(() => import('./pages/Support/SupportTicketPage'))
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'))
const AdminSupportPage = lazy(() => import('./pages/Admin/AdminSupportPage'))
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'))
const UsersPage = lazy(() => import('./pages/Admin/UsersPage'))
const ProvidersPage = lazy(() => import('./pages/Admin/ProvidersPage'))
const EmailSettingsPage = lazy(() => import('./pages/Admin/EmailSettingsPage'))
const FalStatsPage = lazy(() => import('./pages/Admin/FalStatsPage'))
const PromoCodesPage = lazy(() => import('./pages/Admin/PromoCodesPage'))
const SubscriptionsPlansPage = lazy(() => import('./pages/Admin/SubscriptionsPlansPage'))
const AssistantsPage = lazy(() => import('./pages/Admin/AssistantsPage'))
const AdminAssistantDetail = lazy(() => import('./pages/Admin/AdminAssistantDetail'))
const AssistantsSkillsPage = lazy(() => import('./pages/Admin/AssistantsSkillsPage'))
const FinancePage = lazy(() => import('./pages/Admin/FinancePage'))
const PricingPage = lazy(() => import('./pages/Admin/PricingPage'))
const SecurityPage = lazy(() => import('./pages/Admin/SecurityPage'))
const YooKassaPage = lazy(() => import('./pages/Admin/YooKassaPage'))
const StripePage = lazy(() => import('./pages/Admin/StripePage'))
const PaymentResultPage = lazy(() => import('./pages/Payments/PaymentResultPage'))
const PublicOfferPage = lazy(() => import('./pages/PublicOffer/PublicOfferPage'))
const PrivacyPage = lazy(() => import('./pages/Privacy/PrivacyPage'))
const ErrorsPage = lazy(() => import('./pages/Admin/ErrorsPage'))
const LogsPage = lazy(() => import('./pages/Admin/LogsPage'))
const AnalyticsPage = lazy(() => import('./pages/Admin/AnalyticsPage'))

// Minimal loading state while chunk loads
function PageFallback() {
  const { t } = useTranslation()
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--md-sys-color-background, #FFFBFE)',
      color: 'var(--md-sys-color-on-background, #1C1B1F)',
      fontSize: 14,
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 32, height: 32,
          border: '2px solid var(--md-sys-color-outline-variant, #CAC4D0)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'kmin-spin 0.6s linear infinite',
          margin: '0 auto 16px',
        }} />
        {t('common.loading')}
        <style>{`@keyframes kmin-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  )
}

function App() {
  const { isAuthenticated, isGuest } = useAuthStore()
  const hasAppAccess = isAuthenticated || isGuest

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/home" /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/home" /> : <RegisterPage />}
        />
        <Route
          path="/verify"
          element={<VerifyPage />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
        <Route path="/auth/vk/callback" element={<VkAuthCallbackPage />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallbackPage />} />
        <Route path="/public-offer" element={<PublicOfferPage />} />
        <Route path="/terms" element={<Navigate to="/public-offer" replace />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookie-policy" element={<Navigate to="/privacy#cookies" replace />} />

        {/* Landing page (standalone, no layout) */}
        <Route
          path="/"
          element={hasAppAccess ? <Navigate to="/home" /> : <LandingPage />}
        />
        <Route path="/designers" element={<DesignersLandingPage />} />



        {/* Payment redirect pages */}
        <Route path="/payment/success/:orderId" element={<PaymentResultPage />} />
        <Route path="/payment/fail/:orderId" element={<PaymentResultPage />} />

        {/* Protected routes */}
        <Route element={<Layout />}>
          <Route path="/chat" element={<AppRoute><ChatPage /></AppRoute>} />
          <Route path="/home" element={<AppRoute><HomePage /></AppRoute>} />
          <Route path="/studio" element={<AppRoute><StudioPage /></AppRoute>} />
          <Route path="/chat/:chatId" element={<AppRoute><ChatPage /></AppRoute>} />
          <Route path="/tariffs" element={<Navigate to="/balance" replace />} />
          <Route path="/balance" element={<ProtectedRoute><BalancePage /></ProtectedRoute>} />
          <Route path="/ai-assistant" element={<AppRoute><AIAssistantPage /></AppRoute>} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
          <Route path="/ai-assistant/:assistantId" element={<AppRoute><AIChatPage /></AppRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/support/new" element={<ProtectedRoute><SupportNewPage /></ProtectedRoute>} />
          <Route path="/support/:id" element={<ProtectedRoute><SupportTicketPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/library" element={<AppRoute><LibraryHubPage /></AppRoute>} />
          <Route path="/library/examples" element={<AppRoute><LibraryPage /></AppRoute>} />
          <Route path="/product-cards" element={<AppRoute><ProductCardsPage /></AppRoute>} />
          <Route path="/illustration" element={<AppRoute><IllustrationPage /></AppRoute>} />
          <Route path="/ideogram" element={<AppRoute><IdeogramStudioPage /></AppRoute>} />
          <Route path="/infographic-editor" element={<AppRoute><InfographicEditorPage /></AppRoute>} />
          <Route path="/video" element={<AppRoute><VideoPage /></AppRoute>} />
          <Route path="/video-content" element={<AppRoute><VideoContentPage /></AppRoute>} />
          <Route path="/ai-avatar/:tab?" element={<AppRoute><AiAvatarPage /></AppRoute>} />
          <Route path="/help" element={<AppRoute><HelpPage /></AppRoute>} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="email" element={<EmailSettingsPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="subscriptions" element={<SubscriptionsPlansPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="stripe" element={<StripePage />} />
            <Route path="yookassa" element={<YooKassaPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="fal" element={<FalStatsPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="logs" element={<LogsPage />} />
            <Route path="errors" element={<ErrorsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
            <Route path="assistants" element={<AssistantsPage />} />
            <Route path="assistants/:id" element={<AdminAssistantDetail />} />
            <Route path="skills" element={<AssistantsSkillsPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
